/**
 * Preload All Moon LOD Levels Hook
 * Preloads all texture sets in the background for Moon
 */

import { useEffect, useRef, useState } from 'react'
import { useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { getTilePaths, TILE_GRID } from '../textures/tileConfig'
import { setCachedTextureArray, hasTextureArray } from '../textures/textureCache'

/**
 * Create a Data3DTexture array from loaded tiles
 */
async function createTextureArrayFromPaths(paths, columns, rows, gl) {
  const textureLoader = new THREE.TextureLoader()
  
  // Load all tiles
  const loadedTextures = await Promise.all(
    paths.map(path => 
      new Promise((resolve, reject) => {
        textureLoader.load(
          path,
          (tex) => {
            tex.colorSpace = THREE.SRGBColorSpace
            tex.generateMipmaps = false
            tex.minFilter = THREE.LinearFilter
            tex.magFilter = THREE.LinearFilter
            resolve(tex)
          },
          undefined,
          reject
        )
      })
    )
  )

  const firstTile = loadedTextures[0]
  if (!firstTile.image) return null

  const tileSize = firstTile.image.width
  const depth = loadedTextures.length
  const data = new Uint8Array(tileSize * tileSize * depth * 4)

  // Copy each tile into the array
  loadedTextures.forEach((tileTex, index) => {
    if (!tileTex.image) return

    const canvas = document.createElement('canvas')
    canvas.width = tileSize
    canvas.height = tileSize
    let ctx = canvas.getContext('2d', { willReadFrequently: true })
    
    ctx.clearRect(0, 0, tileSize, tileSize)
    ctx.drawImage(tileTex.image, 0, 0, tileSize, tileSize)
    
    const imageData = ctx.getImageData(0, 0, tileSize, tileSize)
    const offset = index * tileSize * tileSize * 4
    data.set(imageData.data, offset)

    // CRITICAL: Proper GPU memory cleanup
    tileTex.dispose()
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    // Force GPU context flush
    try {
      ctx.getImageData(0, 0, 1, 1)
    } catch (e) { }
    // Release context reference
    ctx = null
    canvas.width = 0
    canvas.height = 0
    canvas.remove()
  })

  const texture3D = new THREE.Data3DTexture(data, tileSize, tileSize, depth)
  texture3D.format = THREE.RGBAFormat
  texture3D.type = THREE.UnsignedByteType
  texture3D.colorSpace = THREE.SRGBColorSpace
  texture3D.minFilter = THREE.LinearFilter
  texture3D.magFilter = THREE.LinearFilter
  texture3D.wrapS = THREE.ClampToEdgeWrapping
  texture3D.wrapT = THREE.ClampToEdgeWrapping
  texture3D.wrapR = THREE.ClampToEdgeWrapping
  texture3D.anisotropy = 16
  texture3D.unpackAlignment = 1
  texture3D.needsUpdate = true

  if (gl.initTexture) {
    gl.initTexture(texture3D)
  }

  return texture3D
}

/**
 * Preload all Moon LOD levels in the background
 * @param {Array<string>} lodLevels - Array of LOD level names to preload
 * @param {boolean} enabled - Whether preloading is enabled
 */
export function usePreloadAllLODs(lodLevels = [], enabled = true) {
  const { gl } = useThree()
  const [preloadedCount, setPreloadedCount] = useState(0)
  const [currentLoadingItem, setCurrentLoadingItem] = useState('')
  const isPreloadingRef = useRef(false)

  useEffect(() => {
    if (!enabled || isPreloadingRef.current || lodLevels.length === 0) {
      return
    }

    isPreloadingRef.current = true

    const preloadSequentially = async () => {
      let totalLoaded = 0
      const totalToLoad = lodLevels.length

      for (const lodLevel of lodLevels) {
        const gridConfig = TILE_GRID[lodLevel]
        if (!gridConfig) continue

        setCurrentLoadingItem(`Moon ${lodLevel}`)
        
        // Check if already cached
        if (hasTextureArray(lodLevel)) {
          totalLoaded++
          setPreloadedCount(totalLoaded)
          continue
        }

        try {
          const paths = getTilePaths(lodLevel)
          
          const texture3D = await createTextureArrayFromPaths(
            paths,
            gridConfig.columns,
            gridConfig.rows,
            gl
          )

          if (texture3D) {
            setCachedTextureArray(lodLevel, texture3D)
            totalLoaded++
            setPreloadedCount(totalLoaded)
          }
        } catch (error) {
          // Silent error handling
        }
      }

      setCurrentLoadingItem('')
      isPreloadingRef.current = false
    }

    preloadSequentially()

    return () => {
      // Textures stay in cache
    }
  }, [lodLevels, enabled])

  return {
    isPreloading: isPreloadingRef.current,
    preloadedCount: preloadedCount,
    currentLoadingItem: currentLoadingItem
  }
}
